import React from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FormApi } from 'final-form';
import { Box } from '@mui/material';

import { LoadingOverlay, Typography } from '@occc/ui-design-system';
import { ProductPurchaseConfigure } from '@occc-app/routes/Offer/components/ProductPurchaseConfigure';
import { SpendBonusConfiguration } from '@occc-app/routes/Offer/components/SpendBonusConfiguration';
import { OfferConfigurationWizardFormLayout } from '@occc-app/routes/CampaignComponents/components/offer-configuration-wizard-form-layout';
import { OfferSelect } from '@occc-app/routes/CampaignComponents/components/OfferSelect';
import OfferTypeSelectField from '@occc-app/routes/CampaignComponents/components/OfferTypeSelect';
import { Offer_Engagement_Type, OfferFilter, Publication_Status_Type } from '@occc/graphql';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { ProductChallengeConfiguration } from '@occc-app/routes/Challenge/components/ProductChallengeConfiguration';
import { Redemption } from '@occc-app/routes/Reward/components/Redemption';
import { CampaignComponentFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentFooterActions';
import { SweepstakesEntryAlert } from '@occc-app/routes/CampaignComponents/components/SweepstakesEntryAlert';
import { useChainId } from '@occc-app/Chain/hooks';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';
import { CampaignComponentReadOnlyFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentReadOnlyFooterActions';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';
import { OfferEarningMechanismConfigurationSection } from '@occc-app/offerHelpers/components/offer-earning-mechanism-configuration.section';
import { OfferConfigurationSection } from '@occc-app/offerHelpers/components/offer-configuration-section';
import {
  MinimumPurchaseConstraints,
  PaymentMethodConstraint,
  RequiresActivationConstraint,
} from '@occc/feat-constraints';

import { OfferNameAndDescriptionConfiguration } from '@occc-app/routes/Offer/components/OfferNameAndDescriptionConfiguration';
import { OfferFulfillmentPriorityField } from '@occc-app/offerHelpers/components/OfferFulfillmentPriorityField';
import { validateConfiguration } from './utils';
import { RewardConfiguration } from './RewardConfiguration';
import {
  SweepstakesConfigureFormInitialValues,
  SweepstakesConfigureFormValues,
  SweepstakesType,
} from './types';
import { sweepstakesTypes } from './constants';
import { StayVisibleWhileInProgress } from './components/StayVisibleWhileInProgress';
import { SweepstakesFormValuesWatcher } from './components/SweepstakesFormValuesWatcher';

import { ScanDataConfiguration } from '../CampaignComponents/components/ScanDataConfiguration/ScanDataConfiguration';
import { CustomData } from '../CampaignComponents/components/CustomData/CustomData';
import { configureFormCustomDataSubtitle } from '../../offerHelpers/copies';

export type ConfigureFormProps = {
  onSubmit: (
    values: SweepstakesConfigureFormValues,
    form: FormApi<SweepstakesConfigureFormValues, SweepstakesConfigureFormInitialValues>,
  ) => void;
  onCancelClick: (hasUnsavedChanges?: boolean) => void;
  initialValues: SweepstakesConfigureFormInitialValues;
  loading: boolean;
  currentFlowType: Flow_Types;
  isFormEditable: boolean;
  onNextStepButtonClick?: () => void;
};

export const ConfigureForm = ({
  onSubmit,
  initialValues,
  onCancelClick,
  loading,
  currentFlowType,
  isFormEditable,
  onNextStepButtonClick,
}: ConfigureFormProps) => {
  const chainId = useChainId();

  const makeSweepstakesEntryFilter: () => OfferFilter = React.useCallback(() => {
    return {
      chainIdEquals: chainId,
      engagementTypeEquals: Offer_Engagement_Type.SweepstakesEntry,
      deleted: false,
      publicationStatusEquals: Publication_Status_Type.Published,
    };
  }, [chainId]);

  const renderForm: FormRenderProps<SweepstakesConfigureFormValues>['render'] = React.useCallback(
    ({
      handleSubmit,
      submitting,
      values,
      form,
      dirty,
    }: FormRenderProps<SweepstakesConfigureFormValues>) => {
      const handleCancelClick = () => {
        onCancelClick(dirty);
      };

      const formFooterActionButtons = isFormEditable ? (
        <CampaignComponentFooterActions
          currentFlowType={currentFlowType}
          onCancel={handleCancelClick}
          isSubmitDisabled={submitting || (currentFlowType === Flow_Types.Update && !dirty)}
        />
      ) : (
        <CampaignComponentReadOnlyFooterActions
          onCancel={handleCancelClick}
          onNextStepClick={onNextStepButtonClick}
        />
      );

      const isPaymentMethodAvailable = [
        SweepstakesType.ProductPurchase,
        SweepstakesType.VarietyChallenge,
        SweepstakesType.SpendBonus,
      ].some((type) => type === values.sweepstakesType);

      const isMinimumPurchaseConstraintSupported =
        values.sweepstakesType === SweepstakesType.VarietyChallenge &&
        values.productChallenge?.earningMechanism === 'varietyPurchase';

      return (
        <OfferForm dirty={!submitting && dirty} handleSubmit={handleSubmit}>
          <OfferEarningMechanismConfigurationSection>
            <OfferTypeSelectField
              isChangeButtonVisible={isFormEditable}
              items={sweepstakesTypes}
              name='sweepstakesType'
            />
            {values.sweepstakesType && (
              <>
                <Box mb={2}>
                  <OfferNameAndDescriptionConfiguration entityName='Sweepstake' />
                  <OfferFulfillmentPriorityField mt={2} />
                </Box>
                <Typography variant='subtitle1' paragraph>
                  Give members sweepstakes entry
                </Typography>

                <OfferSelect
                  makeOffersFilter={makeSweepstakesEntryFilter}
                  label='Select Award'
                  name='awardVouchers.voucheredOffer'
                />

                <Box my={2}>
                  <SweepstakesEntryAlert />
                </Box>

                {values.sweepstakesType === SweepstakesType.ProductPurchase && (
                  <ProductPurchaseConfigure />
                )}
                {values.sweepstakesType === SweepstakesType.VarietyChallenge && (
                  <ProductChallengeConfiguration />
                )}
                {values.sweepstakesType === SweepstakesType.SpendBonus && (
                  <SpendBonusConfiguration />
                )}
                {values.sweepstakesType === SweepstakesType.Reward && <RewardConfiguration />}
              </>
            )}
          </OfferEarningMechanismConfigurationSection>

          {values.sweepstakesType && (
            <OfferConfigurationWizardFormLayout actions={formFooterActionButtons}>
              <OfferConfigurationSection title='Limits'>
                <Redemption values={values} />
              </OfferConfigurationSection>

              <OfferConfigurationSection title='Requirements'>
                {isPaymentMethodAvailable && (
                  <PaymentMethodConstraint
                    values={values}
                    isPaymentInstrumentConstraintEnabled={false}
                  />
                )}

                {isMinimumPurchaseConstraintSupported && (
                  <MinimumPurchaseConstraints values={values} />
                )}

                {values.sweepstakesType !== SweepstakesType.Reward && (
                  <RequiresActivationConstraint values={values.activation} />
                )}

                <StayVisibleWhileInProgress
                  sweepstakesType={values.sweepstakesType}
                  earningMechanism={values.productChallenge?.earningMechanism}
                />
              </OfferConfigurationSection>

              <OfferConfigurationSection title='Advanced Offer Settings' mb={0}>
                <CustomData values={values.customData} subtitle={configureFormCustomDataSubtitle} />
                <ScanDataConfiguration
                  values={values.scanDataConfiguration}
                  submitting={submitting}
                />
              </OfferConfigurationSection>
            </OfferConfigurationWizardFormLayout>
          )}

          <SweepstakesFormValuesWatcher values={values} form={form} />
        </OfferForm>
      );
    },
    [
      isFormEditable,
      currentFlowType,
      onNextStepButtonClick,
      makeSweepstakesEntryFilter,
      onCancelClick,
    ],
  );

  return (
    <Box position='relative'>
      <CanEditFormFields isEditable={isFormEditable}>
        <Form<SweepstakesConfigureFormValues, SweepstakesConfigureFormInitialValues>
          onSubmit={onSubmit}
          render={renderForm}
          mutators={{
            ...arrayMutators,
            setFieldValue: (args, state, utils) => {
              // name, value
              utils.changeValue(state, args[0], () => args[1]);
            },
          }}
          initialValues={initialValues}
          validate={validateConfiguration}
        />
      </CanEditFormFields>
      {loading && <LoadingOverlay rounded />}
    </Box>
  );
};
